import { useEffect, useState } from 'react';
import { View } from 'react-native';
import { Text } from '@/components/Text';
import { TextLink } from '@/components/TextLink';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { useStoreState } from '@/stores/store';
import { Checkout, Portal } from './types';
import {
  initializeChargebee,
  loadChargebeeScript,
  openCheckoutOrPortal,
  openPortal,
  unloadChargebeeScript,
  openCheckout,
} from './chargebeeUtil';
import { logError } from '@/utils/logHandling';
import { constants } from '@/config/constants';
import { PromotePage } from './PromotePage';
import { getPermissions } from '@/api/services/user';
import { styles } from './styles';

type PortalType = Portal & { category: 'portal' };
type CheckoutType = Checkout & { category: 'checkout' };

export const LicenseBooking = () => {
  const [chargebeeLoaded, setChargebeeLoaded] = useState(false);

  const [portalOrCheckout, setPortalOrCheckout] = useState<
    PortalType | CheckoutType | null
  >(null);
  const expiresAt = useStoreState(
    (state) => state.subscriptions[state.subscriptions.length - 1]?.expiresAt,
  );

  useEffect(() => {
    loadChargebeeScript()
      .then(() => {
        setChargebeeLoaded(true);
      })
      .catch((error) => {
        logError('Error loading Chargebee script:', error);
      });

    return () => {
      unloadChargebeeScript();
      setChargebeeLoaded(false);
    };
  }, []);

  useEffect(() => {
    if (!chargebeeLoaded) {
      logError('Chargebee script not loaded yet');
      return;
    }

    initializeChargebee(constants.chargebeeSite ?? '');

    loadChargebee();
  }, [chargebeeLoaded]);

  const loadChargebee = () => {
    const response = openCheckoutOrPortal();
    response.then((response) => {
      if (response.data[0].type === 'portal') {
        setPortalOrCheckout({ category: 'portal', ...response.data[0].portal });
      } else {
        setPortalOrCheckout({
          ...response.data[0].pages.filter(
            (c) => c.plan === 'patient_aph_3_months',
          )[0].page,
          category: 'checkout',
        });
      }
    });
  };

  const onBookingSuccessful = () => {
    loadChargebee();
    getPermissions();
  };

  const formatUnixTimestamp = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}.${month}.${year}`;
  };

  return (
    <View style={styles.wrapper}>
      {portalOrCheckout?.category === 'portal' && (
        <View style={styles.portalContainer}>
          <Text style={styles.portalTitle}>Buchung verwalten</Text>
          <View style={styles.infoSection}>
            <Text style={styles.infoText}>
              Ihre aktuelle Lizenz endet zum:{' '}
              <Text style={styles.bold}>{formatUnixTimestamp(expiresAt)}</Text>
            </Text>
            <Text style={styles.infoTextCenter}>
              Ihre Buchung verlängert sich{' '}
              <Text style={styles.bold}>nicht</Text> automatisch. Hier können
              Sie Ihre Lizenz verlängern, sobald diese abgelaufen ist. Oder
              bestellen Sie bereits heute Ihre Lizenzverlängerung bei uns vor,
              ganz einfach per Telefon unter 089 24886 4440 oder E-Mail an{' '}
              <TextLink href="mailto:info@neolexon.de">
                info@neolexon.de
              </TextLink>
            </Text>
          </View>
          <PressableNeolexon onPress={() => openPortal(portalOrCheckout)}>
            <View style={styles.manageButton}>
              <Text style={styles.manageButtonText}>
                Buchungsdetails und Rechnungen
              </Text>
            </View>
          </PressableNeolexon>
        </View>
      )}
      {portalOrCheckout?.category === 'checkout' && (
        <PromotePage
          onBookingClick={() =>
            openCheckout(portalOrCheckout, onBookingSuccessful)
          }
        />
      )}
    </View>
  );
};
